import classNames from 'classnames';
import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import Badge from '../../components/Badge';
import Button from '../../components/Button';
import Card from '../../components/Card';
import MetaList, { MetaListItem } from '../../components/MetaList';
import { gql } from '../../graphql/generated/gql';
import dockyBlank from '../../images/docky-blank.svg';
import { adminToasts } from '../../toasts/toasts';
import { UIActions } from '../../ui/uiSlice';
import * as styles from './AdminApiToken.module.scss';

const API_TOKEN_QUERY = gql(/** @complexity 1 */ `
  query apiToken($id: ID!) {
    apiToken(userId: $id)
  }
`);

const REGENERATE_API_TOKEN = gql(/** @complexity 2 */ `
  mutation regenerateApiToken($id: ID!, $token: String!) {
    regenerateApiToken(userId: $id, token: $token)
  }
`);

type AdminApiTokenProps =
  | {
      id: string;
      name: string;
      fakeToken?: undefined;
    }
  | { id?: undefined; name: string; fakeToken: string };

const AdminApiToken = (props: AdminApiTokenProps) => {
  const { id, name } = props;
  const [isTokenRevealed, setIsTokenRevealed] = useState(false);

  const dispatch = useAppDispatch();

  const { data } = useQuery(API_TOKEN_QUERY, {
    variables: { id: id! },
    skip: !id || !isTokenRevealed,
  });

  const [regenerateToken] = useMutation(REGENERATE_API_TOKEN);

  const shouldUseFake = !data && !props.id;
  const showToken = isTokenRevealed && data?.apiToken;

  const onRegenerateToken = () => {
    if (!id || !data?.apiToken) {
      return;
    }
    regenerateToken({
      variables: { id, token: data?.apiToken },
      update: (proxy, result) => {
        const currentData = proxy.readQuery({
          query: API_TOKEN_QUERY,
          variables: { id },
        });

        if (currentData) {
          proxy.writeQuery({
            query: API_TOKEN_QUERY,
            variables: { id },
            data: {
              apiToken: result.data?.regenerateApiToken,
            },
          });
        }
      },
    });
  };

  const handleCopyButtonClick = async () => {
    if (data?.apiToken) {
      await navigator.clipboard.writeText(data.apiToken);
      dispatch(UIActions.pushToast(adminToasts.tokenCopied));
    }
  };

  return (
    <Card noPadding>
      <div
        className={classNames(styles.wrapper, {
          [styles.fakeWrapper]: shouldUseFake,
        })}
      >
        <div className={styles.img}>
          <img src={dockyBlank} height="16" alt="Slab Logo" />
        </div>
        <div className={styles.content}>
          <div className={styles.nameWrapper}>
            <div className={styles.name}>{name}</div>
            <Badge className={styles.badge} color="pink" inline={false}>
              BOT
            </Badge>
          </div>
          {shouldUseFake ? (
            <div className={styles.subtitle}>{props.fakeToken}</div>
          ) : (
            <>
              {!showToken ? (
                <Button
                  variant="secondary"
                  onClick={() => setIsTokenRevealed(true)}
                >
                  Reveal token
                </Button>
              ) : (
                <div className={styles.tokenWithControls}>
                  {data?.apiToken}
                  <MetaList>
                    <MetaListItem>
                      <Button variant="link" onClick={handleCopyButtonClick}>
                        Copy
                      </Button>
                    </MetaListItem>
                    <MetaListItem>
                      <Button
                        variant="link"
                        onClick={() => onRegenerateToken()}
                      >
                        Regenerate
                      </Button>
                    </MetaListItem>
                  </MetaList>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

export default AdminApiToken;
